import React from 'react';
import { AMENITIES } from '../../../constants/propertyConstants';

const Step4Features = ({ formData, onChange }) => {
    const toggleAmenity = (amenityId) => {
        const current = formData.amenityIds || [];
        const updated = current.includes(amenityId)
            ? current.filter(id => id !== amenityId)
            : [...current, amenityId];
        onChange('amenityIds', updated);
    };

    const groupedAmenities = AMENITIES.reduce((acc, amenity) => {
        if (!acc[amenity.category]) {
            acc[amenity.category] = [];
        }
        acc[amenity.category].push(amenity);
        return acc;
    }, {});

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-soil-900 mb-4">Features & Amenities</h3>
                <p className="text-sm text-soil-600 mb-6">
                    Select all features and amenities available at your property
                </p>
            </div>

            {/* Property Features */}
            <div>
                <h4 className="text-md font-medium text-soil-900 mb-4">Property Features</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Parking */}
                    <div className="border border-soil-100 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-3">
                            <input
                                type="checkbox"
                                id="hasParking"
                                checked={formData.hasParking || false}
                                onChange={(e) => onChange('hasParking', e.target.checked)}
                                className="h-5 w-5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                            />
                            <label htmlFor="hasParking" className="text-sm font-medium text-soil-600 cursor-pointer">
                                Parking Available
                            </label>
                        </div>
                        {formData.hasParking && (
                            <input
                                type="number"
                                min="1"
                                value={formData.parkingSpaces || ''}
                                onChange={(e) => onChange('parkingSpaces', parseInt(e.target.value))}
                                className="w-full px-3 py-2 border border-soil-100 rounded-lg text-sm"
                                placeholder="Number of parking spaces"
                            />
                        )}
                    </div>

                    {/* Elevator */}
                    <div className="flex items-center space-x-3 p-4 border border-soil-100 rounded-lg">
                        <input
                            type="checkbox"
                            id="hasElevator"
                            checked={formData.hasElevator || false}
                            onChange={(e) => onChange('hasElevator', e.target.checked)}
                            className="h-5 w-5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                        />
                        <label htmlFor="hasElevator" className="text-sm font-medium text-soil-600 cursor-pointer">
                            Elevator
                        </label>
                    </div>

                    {/* Security */}
                    <div className="flex items-center space-x-3 p-4 border border-soil-100 rounded-lg">
                        <input
                            type="checkbox"
                            id="hasSecurity"
                            checked={formData.hasSecurity || false}
                            onChange={(e) => onChange('hasSecurity', e.target.checked)}
                            className="h-5 w-5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                        />
                        <label htmlFor="hasSecurity" className="text-sm font-medium text-soil-600 cursor-pointer">
                            24/7 Security
                        </label>
                    </div>

                    {/* WiFi */}
                    <div className="flex items-center space-x-3 p-4 border border-soil-100 rounded-lg">
                        <input
                            type="checkbox"
                            id="hasWifi"
                            checked={formData.hasWifi || false}
                            onChange={(e) => onChange('hasWifi', e.target.checked)}
                            className="h-5 w-5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                        />
                        <label htmlFor="hasWifi" className="text-sm font-medium text-soil-600 cursor-pointer">
                            WiFi
                        </label>
                    </div>

                    {/* Generator */}
                    <div className="flex items-center space-x-3 p-4 border border-soil-100 rounded-lg">
                        <input
                            type="checkbox"
                            id="hasGenerator"
                            checked={formData.hasGenerator || false}
                            onChange={(e) => onChange('hasGenerator', e.target.checked)}
                            className="h-5 w-5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                        />
                        <label htmlFor="hasGenerator" className="text-sm font-medium text-soil-600 cursor-pointer">
                            Backup Generator
                        </label>
                    </div>

                    {/* Water Backup */}
                    <div className="flex items-center space-x-3 p-4 border border-soil-100 rounded-lg">
                        <input
                            type="checkbox"
                            id="hasWaterBackup"
                            checked={formData.hasWaterBackup || false}
                            onChange={(e) => onChange('hasWaterBackup', e.target.checked)}
                            className="h-5 w-5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                        />
                        <label htmlFor="hasWaterBackup" className="text-sm font-medium text-soil-600 cursor-pointer">
                            Water Backup
                        </label>
                    </div>
                </div>
            </div>

            {/* Amenities by Category */}
            <div>
                <h4 className="text-md font-medium text-soil-900 mb-4">Additional Amenities</h4>

                {Object.entries(groupedAmenities).map(([category, amenities]) => (
                    <div key={category} className="mb-6">
                        <h5 className="text-sm font-medium text-soil-600 mb-3 capitalize">
                            {category}
                        </h5>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {amenities.map((amenity) => {
                                const isSelected = (formData.amenityIds || []).includes(amenity.id);
                                return (
                                    <button
                                        key={amenity.id}
                                        type="button"
                                        onClick={() => toggleAmenity(amenity.id)}
                                        className={`
                                            p-3 rounded-lg border-2 text-sm font-medium transition-all
                                            ${isSelected
                                                ? 'border-highland-500 bg-highland-50 text-highland-600'
                                                : 'border-soil-100 bg-white text-soil-600 hover:border-soil-400'
                                            }
                                        `}
                                    >
                                        {amenity.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="bg-highland-50 border border-highland-100 rounded-lg p-4">
                <p className="text-sm text-highland-600">
                    <strong>Selected:</strong> {(formData.amenityIds || []).length} amenities
                </p>
            </div>
        </div>
    );
};

export default Step4Features;
